(ns hypnos.dme.events
  "Events for the backoffice application."
  (:require ["aws-amplify" :as amp :refer (Auth)]
            [hypnos.dme.db :as db]
            [hypnos.dme.routes :as routes]
            [hypnos.graphql.queries :as queries]
            [hypnos.graphql.subscriptions :as subs]
            [hypnos.logging :as log]
            [hypnos.translator.catalog :as t-c]
            [hypnos.translator.organization :as t-o]
            [hypnos.translator.patient :as t-p]
            [medley.core :as mc]
            [re-frame.core :as rf]))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Identities

;; TODO: make this synamic, but getting the classes
(def ^{:private true} html-classes-contracted
  "has-aside-left has-aside-mobile-transition has-navbar-fixed-top")

(def ^{:private true} html-classes-expanded
  "has-aside-left has-aside-mobile-transition has-navbar-fixed-top has-aside-expanded")

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Helpers

(defn- reset-entities
  "Reset the entities"
  [db]
  (-> db
      ;; (assoc-in [:ui :loading?] false)
      (assoc :patient nil)
      (assoc :portrait {:upload-status nil
                        :file          nil
                        :issues        []})
      (assoc :recommendations-with-note {:recommendations      nil
                                         :recommendations-note nil})
      (assoc :communication {:status  nil
                             :message nil})
      (assoc :referral nil)))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Util events

(rf/reg-event-fx
 :dme/noop
 (fn [{db :db} & _]
   {:db (assoc-in db [:ui :loading?] false)}))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; DB

(rf/reg-event-fx
 :dme/initialize-app
 (fn [{db :db} _]
   {:db (merge db db/default-db)}))

(rf/reg-event-fx
 :dme/logout
 (fn [{_db :db} _]
   (.signOut Auth)
   {:db db/default-db}))

(rf/reg-event-fx
  :dme/subscribe-recommend-masks
  (fn [{:keys [db]} _]
    (when-let [consult-id  (some-> db :patient :patient/last-consult :consult/id str)]
      (rf/dispatch
        [:amplify/subscribe
         :onRecommendMasks
         subs/on-recommend-masks
         {:consultId consult-id}
         (fn [_] (.reload js/location))
         (fn [e] (.dir js/console (clj->js e)))]))
    {}))

(rf/reg-event-fx
  :dme/unsubscribe-recommend-masks
  (fn [_ _]
    (rf/dispatch [:amplify/unsubscribe :onRecommendMasks])
    {}))

(rf/reg-event-fx
  :dme/redirect-to-patient-page-with-id
  (fn [{:keys [db]} _]
    (when-let [patient-id (some-> db :patient :patient/id str)]
      (let [path  (routes/path-for :patient-panel :patient-id patient-id)
            path' (.-pathname js/location)]
        (when (not= path path') (set! (.-href js/location) path))))
    {}))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Error Handling

(rf/reg-event-db
 :dme/set-error
 (fn [db [_ {:keys [_data errors]}]]
   (let [error (first errors)]
     (-> db
         (assoc :error {:type    (:errorType error)
                        :message (:message error)})
         (assoc-in [:ui :loading?] false)))))

(rf/reg-event-db
 :dme/clear-error
 (fn [db [_]]
   (-> db
       (assoc :error {:type    nil
                      :message nil})
       (assoc-in [:ui :loading?] false))))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; UI

(rf/reg-event-db
 :dme/set-active-panel
 (fn [db [_ {:keys [name params]}]]
   (-> db
       reset-entities
       (assoc-in [:ui :active-panel] {:name   name
                                      :params params}))))

(rf/reg-event-db
 :dme/toggle-aside
 (fn [db [_]]
   (let [new-state (not (get-in db [:ui :aside :expanded?]))]
     (-> js/document
         (.getElementsByTagName "html")
         first
         .-className
         (set! (if new-state html-classes-expanded html-classes-contracted)))
     (assoc-in db [:ui :aside :expanded?] new-state))))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Catalog

(rf/reg-event-db
 :dme/set-catalog
 (fn [db [_ {:keys [data _errors]}]]
   (let [attrs   (:getCatalog data)
         catalog (mapv t-c/manufacturer->clj (:catalog attrs))]
     (assoc db :catalog catalog))))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Consult(s)

(rf/reg-event-db
 :dme/refresh-consults
 (fn [db [_]]
   (let [limit   (get-in db [:consults :limit])
         org-ids (mapv str (get-in db [:patients :filter :organization-ids]))]
     (rf/dispatch [:amplify/query
                   queries/get-consults
                   {:limit  limit
                    :offset 0
                    :filter {:organization_ids org-ids}}
                   :dme/set-consults
                   :dme/noop])
     (-> db
         (assoc-in [:consults :offset] 0)
         (assoc :patient nil)
         (assoc :portrait {:upload-status nil
                           :file          nil
                           :issues        []})))))

(rf/reg-event-db
 :dme/set-consults
 (fn [db [_ {:keys [data _errors]}]]
   (let [attrs    (:getConsults data)
         consults (mapv t-p/consult-with-patient->clj (:consults attrs))
         offset   (:offset attrs)]
     (-> db
         (assoc :patient nil)
         (assoc :portrait {:upload-status nil
                           :file          nil
                           :issues        []})
         (assoc-in [:consults :consults] consults)
         (assoc-in [:consults :offset] offset)
         (assoc-in [:ui :loading?] false)))))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Patient(s)

(rf/reg-event-db
 :dme/refresh-patient
 (fn [db _]
   (when-let [patient-id (get-in db [:patient :patient/id])]
     (rf/dispatch [:amplify/query
                   queries/get-patient-by-id
                   {:id (str patient-id)}
                   :dme/set-patient
                   :dme/noop]))
   db))

(rf/reg-event-db
 :dme/refresh-patients
 (fn [db _]
   (let [limit          (get-in db [:patients :limit])
         org-ids        (mapv str (get-in db [:patients :filter :organization-ids]))
         query          (get-in db [:patients :query])]
     (rf/dispatch [:amplify/query
                   queries/get-patients
                   {:limit  limit
                    :offset 0
                    :filter {:organization_ids org-ids}
                    :query  query}
                   :dme/set-patients
                   :dme/noop])
     (-> db
         (assoc-in [:patients :offset] 0)
         (assoc :patient nil)
         (assoc :portrait {:upload-status nil
                           :file          nil
                           :issues        []})))))

(rf/reg-event-db
 :dme/set-patients
 (fn [db [_ {:keys [data _errors]}]]
   (let [attrs    (:getPatients data)
         patients (mapv t-p/patient-with-last-consult->clj (:patients attrs))
         offset   (:offset attrs)
         total    (:total attrs)]
     (-> db
         (assoc :patient nil)
         (assoc :portrait {:upload-status nil
                           :file          nil
                           :issues        []})
         (assoc-in [:patients :patients] patients)
         (assoc-in [:patients :offset] offset)
         (assoc-in [:patients :total] total)
         (assoc-in [:ui :loading?] false)))))

(rf/reg-event-db
 :dme/set-patients-filter-scope
 (fn [db [_ scope]]
   (let [limit   (get-in db [:patients :limit])
         org-ids (mapv str (get-in db [:patients :filter :organization-ids]))
         query   (get-in db [:patients :query])]
     (rf/dispatch [:amplify/query
                   queries/get-patients
                   {:limit  limit
                    :offset 0
                    :filter {:organization_ids org-ids}
                    :query  query}
                   :dme/set-patients
                   :dme/noop])
     (-> db
         (assoc :patient nil)
         (assoc :portrait {:upload-status nil
                           :file          nil
                           :issues        []})
         (assoc-in [:patients :offset] 0)
         (assoc-in [:patients :filter :scope] scope)))))

(rf/reg-event-db
 :dme/set-patients-filter-organization-ids
 (fn [db [_ organization-ids]]
   (let [limit   (get-in db [:patients :limit])
         org-ids (mapv str organization-ids)
         query   (get-in db [:patients :query])]
     (rf/dispatch [:amplify/query
                   queries/get-patients
                   {:limit  limit
                    :offset 0
                    :filter {:organization_ids org-ids}
                    :query  query}
                   :dme/set-patients
                   :dme/noop])
     (-> db
         (assoc :patient nil)
         (assoc :portrait {:upload-status nil
                           :file          nil
                           :issues        []})
         (assoc-in [:patients :offset] 0)
         (assoc-in [:patients :filter :organization-ids] organization-ids)))))

(rf/reg-event-db
 :dme/set-patients-query
 (fn [db [_ query]]
   (let [limit   (get-in db [:patients :limit])
         org-ids (mapv str (get-in db [:patients :filter :organization-ids]))]
     (rf/dispatch [:amplify/query
                   queries/get-patients
                   {:limit  limit
                    :offset 0
                    :filter {:organization_ids org-ids}
                    :query  query}
                   :dme/set-patients
                   :dme/noop])
     (-> db
         (assoc :patient nil)
         (assoc :portrait {:upload-status nil
                           :file          nil
                           :issues        []})
         (assoc-in [:patients :offset] 0)
         (assoc-in [:patients :query] query)))))

(rf/reg-event-db
 :dme/set-patient
 (fn [db [_ {:keys [data errors]}]]
   (if errors
     (-> db
         (assoc :error {:type    "NotFound"
                        :message "Patient not found!"})
         (assoc-in [:ui :loading?] false))
     (let [attrs   (first (vals data))
           patient (t-p/patient-with-consults->clj attrs)
           _       (when (and (:patient/id patient)
                              (:patient/image patient))
                     (rf/dispatch [:amplify/download-file
                                   {:id (str (:patient/id patient))}
                                   :dme/set-patient-image]))]
       (-> db
           (assoc :patient patient)
           (assoc :portrait {:upload-status nil
                             :file          nil
                             :issues        []})
           (assoc-in [:ui :loading?] false))))))

(rf/reg-event-db
 :dme/set-patient-image
 (fn [db [_ payload]]
   (-> db
       (assoc :portrait {:upload-status :no-issue
                         :file          payload
                         :issues        []}))))

(rf/reg-event-db
 :dme/set-recommendations-with-note
 (fn [db [_ {:keys [data errors]}]]
   (if errors
     (-> db
         (assoc :error {:type    "NotFound"
                        :message "Patient not found!"})
         (assoc-in [:ui :loading?] false))
     (let [attrs                (first (vals data))
           recommendations      (mapv t-p/recommendation->clj (:recommendations attrs))
           recommendations-note (:recommendations_note attrs)]
       (-> db
           (assoc-in [:recommendations-with-note :recommendations] recommendations)
           (assoc-in [:recommendations-with-note :recommendations-note] recommendations-note)
           (assoc-in [:ui :loading?] false))))))

(rf/reg-event-db
 :dme/reset-patient
 (fn [db [_ _]]
   (rf/dispatch [:dme/unsubscribe-recommend-masks])
   (reset-entities db)))

(rf/reg-event-db
 :dme/set-referral
 (fn [db [_ {:keys [data errors]}]]
   (if errors
     (-> db
         (assoc :error {:type    "NotFound"
                        :message "Referral not found!"})
         (assoc-in [:ui :loading?] false))
     (let [attrs    (first (vals data))
           referral (t-p/patient-with-last-consult->clj attrs)]
       (-> db
           (assoc :referral referral)
           (assoc-in [:ui :loading?] false))))))

(rf/reg-event-db
 :dme/reset-referral
 (fn [db [_ _]]
   (reset-entities db)))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Organization

(rf/reg-event-db
 :dme/set-organization
 (fn [db [_ organization-id]]
   (let [organization (get-in db [:organizations organization-id])]
     (-> db
         (assoc :organization organization)
         (assoc :patient nil)
         (assoc :portrait {:upload-status nil
                           :file          nil
                           :issues        []})
         (assoc-in [:patients :offset] 0)
         (assoc-in [:patients :filter :organization-ids] [organization-id])))))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; Communication

(rf/reg-event-db
 :dme/set-communication-status
 (fn [db [_ status]]
   (-> db
       (assoc-in [:communication :status] status))))

(rf/reg-event-db
 :dme/set-communication
 (fn [db [_ {:keys [data _errors]}]]
   (when-let [patient-id (get-in db [:patient :patient/id])]
     (rf/dispatch [:amplify/query
                   queries/get-patient-by-id
                   {:id (str patient-id)}
                   :dme/set-patient
                   :dme/noop]))
   (-> db
       (assoc-in [:communication :status] (when (:sendCommunication data) :send)))))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;
;;; User Authentication

(rf/reg-event-db
 :dme/set-user
 (fn [db [_ {:keys [data _errors]}]]
   (let [attrs         (:getUser data)
         user          (t-o/user->clj attrs)
         organizations (mc/map-vals first (group-by :organization/id (:user/organizations user)))
         organization  (first (:user/organizations user))]
     ;; check that user is a DME user
     (if (= (:organization/type organization) :organization-type/dme)
       (-> db
           (assoc-in [:auth :user] user)
           (assoc :organizations organizations)
           (assoc :organization organization)
           (assoc-in [:patients :filter :organization-ids] [(:organization/id organization)]))
       (do
         (log/debug "Invalid organization: " (pr-str organization))
         (.signOut Auth)
         {:db db/default-db})))))

(rf/reg-event-db
 :dme/set-session
 (fn [db [_ {:keys [groups]}]]
   (assoc-in db [:auth :groups] groups)))
